import React from 'react'
import { ThemeProvider } from 'styled-components'
import { Auth0Provider, AppState } from '@auth0/auth0-react'
import { MuiThemeProvider, StylesProvider } from '@material-ui/core/styles'
import { CssBaseline } from '@material-ui/core'
import { ReactQueryDevtools } from 'react-query/devtools'

import theme from 'src/styles/theme'
import AppRouter from 'src/AppRouter'
import I18nProvider from 'src/contexts/I18n'
import GlobalStyles from 'src/styles/GlobalStyles'
import { ProfileProvider } from 'src/contexts/Profile'
import { AppStateProvider } from 'src/contexts/AppState'
import { InitDataProvider } from 'src/contexts/InitData'
import { AnalyticsProvider } from 'src/contexts/Analytics'
import { useAuth0IdToken } from 'src/hooks/useAuth0IdToken'
import Security from 'src/modules/RouterComponents/Security'
import ApiClientProvider from 'src/contexts/ApiClientProvider'
import LanguageParamProvider from 'src/contexts/LanguageParamProvider'

const Providers: React.FC = () => {
  const { currentCacheLocation } = useAuth0IdToken()

  const handleAuth0RedirectCallback = (appState: AppState) => {
    if (appState.returnTo) {
      window.history.pushState(null, document.title, appState.returnTo)
    }
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN!}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
      redirectUri={window.location.origin}
      onRedirectCallback={handleAuth0RedirectCallback}
      cacheLocation={currentCacheLocation} // if Cypress is present, cache is "localstorage", otherwise it's "memory"
      useRefreshTokens
    >
      <LanguageParamProvider>
        <AppStateProvider>
          <ApiClientProvider>
            <MuiThemeProvider theme={theme}>
              <ThemeProvider theme={theme}>
                <StylesProvider injectFirst>
                  <GlobalStyles />
                  <CssBaseline />
                  <Security>
                    <InitDataProvider>
                      <I18nProvider>
                        <AnalyticsProvider>
                          <ProfileProvider>
                            <AppRouter />
                          </ProfileProvider>
                        </AnalyticsProvider>
                      </I18nProvider>
                    </InitDataProvider>
                  </Security>
                </StylesProvider>
              </ThemeProvider>
            </MuiThemeProvider>
            <ReactQueryDevtools />
          </ApiClientProvider>
        </AppStateProvider>
      </LanguageParamProvider>
    </Auth0Provider>
  )
}

export default Providers
